import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useModal from '@tenant/utility/hooks/useModal';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import AppGridContainer from '@tenant/core/AppGridContainer';
import useChangeVersion from 'redux/slices/containerApp/hooks/useChangeVersion';
import ImageVersionSelect from '@tenant/core/App/Select/ImageVersionSelect';

const steps = [
  {
    label: 'Change the Image Version of the Container App.',
  },
  {
    label: 'Re-deploy with the changed Version.',
  },
];

const ChangeVersionAction = ({
  containerId,
  appInstanceId,
  open,
  onCloseModal,
  imageName,
  imageId,
  refetchDetail = () => {},
}) => {
  const action = useChangeVersion();
  const { visible, onShow, onClose } = useModal();
  const [newImage, setNewImage] = useState();
  const show = !!(open ?? visible);

  const disabledSubmit = useMemo(() => !newImage?.Id, [newImage]);

  const handleClose = () => {
    onCloseModal ? onCloseModal() : onClose();
  };

  const handleSave = () => {
    if (disabledSubmit) {
      return;
    }
    action.mutate(
      { containerAppId: containerId, appInstanceId, imageId: newImage.Id },
      {
        onSuccess() {
          handleClose();
          setNewImage();
          refetchDetail();
        },
      },
    );
  };

  return (
    <>
      {typeof open === 'undefined' ? (
        <IconButton
          title='Change Image Version'
          size='l'
          onClick={onShow}
          sx={{ pl: 0 }}
        >
          <SettingsBackupRestoreIcon />
        </IconButton>
      ) : (
        ''
      )}
      <Dialog open={show} maxWidth='md' fullWidth>
        <DialogTitle sx={{ fontSize: 20 }}> Change Image Version </DialogTitle>
        <DialogContent>
          <AppGridContainer>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography mb={3} fontSize={13}>
                  After submit all actions will be process
                </Typography>
                <Stepper activeStep={3} orientation='vertical'>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel>{step.label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <DialogContentText>
                The change version may include new features, bug fixes, or
                performance improvements that will be available once the new
                version is running.
              </DialogContentText>

              <Box
                mt={5}
                sx={{ backgroundColor: '#ededed', borderRadius: 2, p: 3 }}
              >
                <AppGridContainer>
                  <Grid item xs={4}>
                    <ListItemText
                      primary='Current Version'
                      secondary={imageName ?? '-'}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <ListItemText
                      primary='Current Version ID'
                      secondary={imageId ?? '-'}
                    />
                  </Grid>
                </AppGridContainer>
              </Box>

              <Box sx={{ mt: 5 }}>
                <ImageVersionSelect
                  label='Select Image Version'
                  fullWidth
                  value={newImage}
                  placeholder='Enter text'
                  appInstanceId={appInstanceId}
                  containerId={containerId}
                  onChange={(e, value) => {
                    setNewImage(value);
                  }}
                />
              </Box>
            </Grid>
          </AppGridContainer>
        </DialogContent>
        <DialogActions>
          <Button disabled={action.isPending} onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            disabled={disabledSubmit}
            loading={action.isPending}
            onClick={handleSave}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(ChangeVersionAction);

ChangeVersionAction.propTypes = {
  containerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  appInstanceId: PropTypes.string,
  open: PropTypes.bool,
  onCloseModal: PropTypes.func,
  imageName: PropTypes.string,
  imageId: PropTypes.string,
  refetchDetail: PropTypes.func,
};
